// ---------------------------------------------------------------------------
// PlanogramNavigation
//
// Pure functions answering "given the active cell and an arrow direction,
// which cell key should become active next?" — for GH #555's arrow-key
// navigation requirement. No React, no DOM; same testability contract as
// PlanogramGridOps/PlanogramSelection.
//
// Two different "next cell" rules are needed because Visual and Tabular
// present the same underlying grid differently:
//   - Visual is a literal 2D grid: Up/Down/Left/Right all just mean
//     row±1/col±1, clamped to [0, rows) x [0, columns), skipping merge
//     FOLLOWER cells (they aren't independently selectable/visible — the
//     merge SOURCE cell at the left edge of the block represents the whole
//     span, same convention PlanogramSelection.visibleKeysFor already uses).
//   - Tabular is one `<tr>` per visible cell, filtered/ordered by
//     `visibleTableRows`. It's a flat LIST, not a 2D grid — so its arrow
//     model is deliberately simpler than Visual's: Down and Right both
//     mean "next row in table order"; Up and Left both mean "previous row
//     in table order". There is no real column axis to move across here
//     (confirmed: this is intentionally NOT Excel's per-column Up/Down —
//     see nextTabularCellKey's own header comment for the full rationale).
// ---------------------------------------------------------------------------

import { CellKey, cellKey, PlanogramGrid } from './types';

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

/** True for a cell that is itself addressable/visible — i.e. NOT a merge follower (the source cell of a merge block IS visible/navigable; only the cells it swallows are not). */
function isVisibleCell(grid: PlanogramGrid, rowIndex: number, colIndex: number): boolean {
    const cell = grid?.[rowIndex]?.[colIndex];
    if (!cell) return false;
    return !(cell.merged && cell.mergeData?.isMerged);
}

/**
 * Visual-view navigation: literal rectangular grid. Returns null if the
 * move would go out of bounds (no wrap-around, matching how Tab/Enter
 * navigation in this codebase already stops at the edges rather than
 * cycling — see useGridEnterNavigation).
 *
 * Merge followers are skipped by continuing to step in the same direction
 * until a visible cell is found (or the edge is hit) — a merge block
 * spans columns, never rows, in this grid model (see PlanogramGridOps:
 * mergeRight/mergeLeft only ever touch `colspan`), so only horizontal
 * movement can land on a "swallowed" cell; vertical movement always lands
 * on a real row/column pair, but the helper is defensive about both axes.
 */
export function nextVisualCellKey(
    grid: PlanogramGrid,
    rows: number,
    columns: number,
    fromRowIndex: number,
    fromColIndex: number,
    direction: ArrowDirection,
): CellKey | null {
    const deltaRow = direction === 'up' ? -1 : direction === 'down' ? 1 : 0;
    const deltaCol = direction === 'left' ? -1 : direction === 'right' ? 1 : 0;

    let row = fromRowIndex;
    let col = fromColIndex;

    // A merge follower's own deltaCol step would just walk across the rest
    // of the same merge block one cell at a time; jump straight to the
    // block's source column first so the very next step in the loop below
    // moves to genuinely the next/previous DISTINCT cell.
    const startingCell = grid?.[row]?.[col];
    if (startingCell?.merged && startingCell.mergeData?.isMerged && startingCell.mergeData.sourceCell) {
        col = startingCell.mergeData.sourceCell.colIndex;
    }

    for (let guard = 0; guard < Math.max(rows, columns) + 1; guard++) {
        row += deltaRow;
        col += deltaCol;

        if (row < 0 || row >= rows || col < 0 || col >= columns) return null;
        if (isVisibleCell(grid, row, col)) return cellKey(row, col);
        // else: landed on a merge follower — keep stepping in the same direction.
    }

    return null;
}

/**
 * *** CHANGED (per explicit request): Tabular navigation is now a flat
 * LINEAR walk over visibleCells, not column-aware Up/Down + same-row
 * Left/Right. ***
 *
 * Previous behavior (now removed): Left/Right moved between columns
 * within the same row; Up/Down jumped to the nearest row sharing the same
 * column, skipping over other columns — i.e. it tried to behave like a
 * real spreadsheet's per-column vertical movement. That's a reasonable
 * model for a real 2D grid, but Tabular doesn't render one — it's one
 * `<tr>` per visible (rowIndex, colIndex) pair, already flattened into a
 * single ordered list (visibleTableRows, row-major: all of row 1's
 * cells, then all of row 2's, etc.). The person asked for the simpler
 * model that matches what's actually on screen: there are only two
 * directions that mean anything in a flat list — "forward" and
 * "backward" — so:
 *   - ArrowDown and ArrowRight both move to the NEXT entry in
 *     visibleCells (table order).
 *   - ArrowUp and ArrowLeft both move to the PREVIOUS entry in
 *     visibleCells (table order).
 * No wrap-around at the ends (returns null), matching every other
 * navigation primitive in this codebase (nextVisualCellKey,
 * useGridEnterNavigation's Tab/Enter).
 */
export function nextTabularCellKey(
    visibleCells: ReadonlyArray<{ rowIndex: number; colIndex: number }>,
    fromRowIndex: number,
    fromColIndex: number,
    direction: ArrowDirection,
): CellKey | null {
    const currentIndex = visibleCells.findIndex(
        c => c.rowIndex === fromRowIndex && c.colIndex === fromColIndex,
    );
    if (currentIndex === -1) return null;

    const step = direction === 'down' || direction === 'right' ? 1 : -1;
    const target = visibleCells[currentIndex + step];
    if (!target) return null;

    return cellKey(target.rowIndex, target.colIndex);
}